import { boolean, number, object, string } from 'yup';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  mapRepliconDateToMidnightUTCString,
  toRepliconDate,
  dateToMidnightUTCString
} from '~/modules/common/dates/convert';
import { getResourceRequestDatesFromProjectDate } from '~/modules/resourcing/common/util';
import { roundToDecimals } from '~/modules/resourcing/common/util/scheduleUtil';
import { getMaxDateForAllocation } from '~/modules/resourcing/components/AllocateResourcePopup/hooks/useAllocateResourcePopupHandler';
import { AllocationDistributionMode, AllocationUpdateBehaviour } from '~/types';
import { useSnackbar } from '~/modules/common/snackbar';
import { useGetScheduleHoursForUserAndDateRange } from '~/modules/resourcing/common/hooks/useGetScheduleHoursForUserAndDateRange';
import {
  ALLOCATION_PERIOD,
  getHoursValueByPeriod
} from '../../AllocationByPeriod';

const validationSchema = object().shape({
  startDate: object()
    .shape({
      year: number().required(),
      month: number().required(),
      day: number().required()
    })
    .required(),
  endDate: object()
    .shape({
      year: number().required(),
      month: number().required(),
      day: number().required()
    })
    .required(),
  isClearAllocationChecked: boolean(),
  selectedAllocationPeriod: string()
    .oneOf(Object.values(ALLOCATION_PERIOD))
    .required()
});

export const useSetResourceAllocationFormState = ({
  existingResourceAllocation,
  project,
  scale,
  userUri,
  onClose,
  onUpdateResourceAllocation
} = {}) => {
  const { formatMessage } = useIntl();
  const { showSnackbar } = useSnackbar();

  const {
    requestStartDate: defaultStartDate,
    requestEndDate: defaultEndDate
  } = getResourceRequestDatesFromProjectDate({
    projectStartDate: project.startDate,
    projectEndDate: project.endDate,
    scale
  });

  const maxEndDate = getMaxDateForAllocation({
    startDate: defaultStartDate,
    endDate: defaultEndDate
  });

  const {
    scheduledHours,
    loading: isScheduleLoading
  } = useGetScheduleHoursForUserAndDateRange({
    userUri,
    dateRange: {
      startDate: dateToMidnightUTCString(defaultStartDate),
      endDate: dateToMidnightUTCString(maxEndDate)
    },
    skip: !userUri
  });

  const totalScheduledHours = scheduledHours || 0;

  const initialValues = useMemo(
    () => ({
      startDate: toRepliconDate(defaultStartDate),
      endDate: toRepliconDate(maxEndDate),
      isClearAllocationChecked: false,
      selectedAllocationPeriod: ALLOCATION_PERIOD.ENTIRE_PERIOD,
      hoursForEntirePeriod:
        totalScheduledHours > 0 ? roundToDecimals(totalScheduledHours) : 0,
      percentageForEntirePeriod: 100,
      hoursPerMonth: '',
      hoursPerWeek: ''
    }),
    [defaultStartDate, maxEndDate, totalScheduledHours]
  );

  const onSubmit = useCallback(
    async values => {
      const {
        startDate,
        endDate,
        selectedAllocationPeriod,
        hoursForEntirePeriod,
        hoursPerMonth,
        hoursPerWeek
      } = values;

      await onUpdateResourceAllocation({
        allocation: {
          id: existingResourceAllocation.id,
          user: {
            userUri
          },
          allocationHoursInDateRange: {
            dateRange: {
              startDate: mapRepliconDateToMidnightUTCString(startDate),
              endDate: mapRepliconDateToMidnightUTCString(endDate)
            },
            hoursPerPeriod:
              parseFloat(
                getHoursValueByPeriod({
                  selectedAllocationPeriod,
                  hoursForEntirePeriod,
                  hoursPerMonth,
                  hoursPerWeek
                })
              ) || 0,
            periodType: selectedAllocationPeriod,
            distributionMode: AllocationDistributionMode.Redistribute
          },
          allocationUpdateBehaviour: AllocationUpdateBehaviour.Replace
        },
        existingResourceAllocation
      });

      showSnackbar &&
        showSnackbar(
          formatMessage({
            id: 'setResourceAllocationDialog.snackbarSuccessMessage'
          })
        );

      onClose();
    },
    [
      existingResourceAllocation,
      userUri,
      formatMessage,
      onClose,
      onUpdateResourceAllocation,
      showSnackbar
    ]
  );

  return {
    initialValues,
    validationSchema,
    onSubmit,
    totalScheduledHours,
    isScheduleLoading
  };
};
